"use client";

import { ArrowUpRight, FileSearch, Folder, GitBranch, Network, Rocket, Terminal as TerminalIcon } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  title: "Workflow Automation",
  description: "Streamline repetitive tasks and manual processes with tailored automation designed around the way your business works.",
  command: "workflow.run({ manualSteps: false })",
  icon: Rocket
}, {
  title: "Data Integration",
  description: "Connect information from spreadsheets, files, platforms, and systems to create a more consistent and reliable flow of business data.",
  command: "data.sync({ src: \"all\", })",
  icon: Network
}, {
  title: "Reporting Automation",
  description: "Automate recurring reports, dashboards, and management information so your team can access updated results faster and with fewer manual steps.",
  command: "generate({schedule:\"monthly\" })",
  icon: GitBranch
}, {
  title: "Data Validation",
  description: "Introduce checks and controls that help identify missing, incorrect, or inconsistent data before it affects reporting or decision-making.",
  command: "validate({ mode: \"strict\", err: true })",
  icon: FileSearch
}, {
  title: "File & Document Automation",
  description: "EAutomate file preparation, document creation, PDF handling, and structured outputs to reduce repetitive administrative work.",
  command: "create({source: \"data\", output:\"pdf\" })",
  icon: Folder
}, {
  title: "Custom Business Tools",
  description: "Build practical internal tools that support your team’s workflow, simplify recurring tasks, and improve operational efficiency.",
  command: "tool.build({type:\"business-process\"})",
  icon: TerminalIcon
}];
export default function Automation() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="14471" id="our-automation-and-technology-capabilities" className="text-foreground py-24 md:py-32 dark bg-accent">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 lg:px-14">
        
        <div ref={header.ref} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end mb-14 md:mb-20 transition-all duration-500 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="md:col-span-7">
            <span className="font-mono text-xs font-semibold tracking-[0.08em] uppercase text-muted-foreground">// AUTOMATION CAPABILITIES</span>
            <h2 className="font-serif font-default mt-4 text-[clamp(2rem,4.5vw,4rem)] font-black leading-[0.96] tracking-[-0.025em]">Our <span className="inline-block px-1.5 py-0.5 bg-muted-foreground text-primary" data-ast-id="f:/src/pages/DataAutomationTechnology/sections/Automation.tsx|sig:kyyrsp" data-ast-start="2661" data-ast-end="2790" contenteditable="false" style={{}}>CAPABILITIES</span> in business automation.</h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg leading-[1.55] text-muted-foreground">We create practical automation and technology solutions that reduce repetitive work, improve accuracy, and make business processes faster, clearer, and easier to manage.</p>
          </div>
        </div>

        
        <div ref={grid.ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, i) => {
          const Icon = category.icon;
          return <div key={category.title} data-index={i} className={`group relative bg-card text-card-foreground border-[1.5px] border-border p-7 flex flex-col shadow-md transition-all duration-200 ease-out hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-lg rounded-lg ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: grid.isVisible ? `${i * 60}ms` : "0ms"
          }}>
                
                <div className="mb-6">
                  <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>

                
                <h3 className="font-serif font-default text-[clamp(1.375rem,2vw,1.875rem)] font-black leading-[1.05] tracking-[-0.015em] mb-5">
                  {category.title}
                </h3>

                
                <div className="h-px bg-border mb-5" />

                
                <p className="text-base leading-[1.6] text-muted-foreground mb-6 flex-1">
                  {category.description}
                </p>

                
                <div className="bg-muted border-[1.5px] border-border px-3 py-2.5 mb-5 flex items-center gap-2 overflow-hidden rounded-lg">
                  <span className="font-mono text-sm font-semibold text-primary shrink-0">
                    {"$"}
                  </span>
                  {i === 5}
                  <code className="font-mono text-[13px] text-foreground truncate !w-[310px]">
                    {category.command}
                  </code>
                </div>

                
                
              </div>;
        })}
        </div>
      </div>
    </section>;
}